import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Center,
  Drawer,
  Group,
  Loader,
  Modal,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCircleCheck,
  IconHash,
  IconHelp,
  IconHourglass,
  IconMessageCircle,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSend,
  IconTag,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { EmptyState } from '@/shared/components/EmptyState';
import { PageHeader } from '@/shared/components/PageHeader';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { fetchList, createOne } from '@/shared/api/jsonapi/client';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@/app/providers/AuthProvider';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/tickets')({ component: TicketsPage });

interface Ticket extends Denormalized {
  type: 'tickets';
  id: string;
  number?: number;
  message?: string;
  note?: string;
  status?: string;
  priority?: number;
  creationDate?: string;
  closedDate?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  answer?: string;
  ticketTag?: { id: string; name?: string } | null;
}

interface TicketMessage extends Denormalized {
  type: 'ticket-messages';
  id: string;
  message?: string;
  creationDate?: string;
  user?: { id: string; firstName?: string; lastName?: string; email?: string } | null;
}

interface TicketTag extends Denormalized {
  type: 'ticket-tags';
  id: string;
  name?: string;
  tag?: string;
}

type StatusFilter = 'all' | 'open' | 'in-progress' | 'resolved' | 'closed';
type PriorityFilter = 'all' | '0' | '1' | '2' | '3';

import { statusColor, priorityColor } from '@/shared/lib/statusColors';

// Label e icon locali, color canonico da statusColors.ts (palette unificata cross-prodotto).
const STATUS_META: Record<string, { label: string; icon: typeof IconHelp }> = {
  open: { label: 'Aperto', icon: IconHelp },
  'in-progress': { label: 'In lavorazione', icon: IconHourglass },
  resolved: { label: 'Risolto', icon: IconCircleCheck },
  closed: { label: 'Chiuso', icon: IconCircleCheck },
};

const PRIORITY_LABEL: Record<number, string> = {
  0: 'Bassa', 1: 'Media', 2: 'Alta', 3: 'Urgente',
};

function statusInfo(s?: string) {
  if (!s) return { label: '—', color: 'gray', icon: IconHelp };
  const meta = STATUS_META[s] ?? { label: s, icon: IconHelp };
  return { ...meta, color: statusColor(s) };
}

function priorityInfo(p?: number) {
  if (p == null) return { label: 'Media', color: priorityColor('medium') };
  return { label: PRIORITY_LABEL[p] ?? String(p), color: priorityColor(p) };
}

function TicketsPage() {
  const qc = useQueryClient();
  const [opened, { open, close }] = useDisclosure();
  const isMobile = useMediaQuery('(max-width: 48em)') ?? false;
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);

  // Create form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<string>('1');
  const [tagId, setTagId] = useState<string | null>(null);

  const ticketsQ = useQuery({
    queryKey: ['my-tickets'],
    queryFn: () => fetchList<Ticket>('tickets', {
      include: ['ticketTag'],
      sort: ['-creationDate'],
      page: { size: 100 },
    }),
  });

  const tagsQ = useQuery({
    queryKey: ['ticket-tags'],
    queryFn: () => fetchList<TicketTag>('ticket-tags', { page: { size: 50 } }),
    staleTime: 5 * 60_000,
  });

  const tickets = useMemo(() => ticketsQ.data?.data ?? [], [ticketsQ.data]);
  const tags = tagsQ.data?.data ?? [];

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return tickets.filter((t) => {
      if (q) {
        const hay = `${t.number ?? ''} ${t.message ?? ''} ${t.note ?? ''} ${t.ticketTag?.name ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'all' && (t.status ?? 'open') !== statusFilter) return false;
      if (priorityFilter !== 'all' && String(t.priority ?? 1) !== priorityFilter) return false;
      return true;
    });
  }, [tickets, debouncedSearch, statusFilter, priorityFilter]);

  const counts = useMemo(() => ({
    all: tickets.length,
    open: tickets.filter((t) => (t.status ?? 'open') === 'open').length,
    'in-progress': tickets.filter((t) => t.status === 'in-progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
    closed: tickets.filter((t) => t.status === 'closed').length,
  }), [tickets]);

  const create = useMutation({
    mutationFn: () => createOne('tickets', {
      attributes: {
        message: `${title.trim()}\n\n${body.trim()}`,
        priority: parseInt(priority, 10),
        status: 'open',
      },
      relationships: tagId ? { ticketTag: { data: { type: 'ticket-tags', id: tagId } } } : undefined,
    }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Ticket inviato' });
      setTitle(''); setBody(''); setTagId(null); setPriority('1');
      close();
      qc.invalidateQueries({ queryKey: ['my-tickets'] });
    },
    onError: (err) => notifications.show({ color: 'red', message: err instanceof Error ? err.message : 'Errore' }),
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title="Help desk"
        description="I tuoi ticket di supporto, conversazioni e aggiornamenti."
        actions={
          <>
            <Tooltip label="Aggiorna">
              <ActionIcon variant="light" size="lg" aria-label="Aggiorna lista ticket" onClick={() => qc.invalidateQueries({ queryKey: ['my-tickets'] })}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Button leftSection={<IconPlus size={16} />} onClick={open}>Nuovo ticket</Button>
          </>
        }
      />

      <Card withBorder padding="md" radius="md">
        <Group wrap="wrap" gap="sm">
          <TextInput
            placeholder="Cerca per numero, testo o tag…"
            leftSection={<IconSearch size={14} />}
            rightSection={search ? (
              <ActionIcon variant="subtle" size="sm" aria-label="Cancella ricerca" onClick={() => setSearch('')}>
                <IconX size={12} />
              </ActionIcon>
            ) : null}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={260}
          />
          <SegmentedControl
            size="xs"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
            data={[
              { value: 'all', label: `Tutti (${counts.all})` },
              { value: 'open', label: `Aperti (${counts.open})` },
              { value: 'in-progress', label: `In lavorazione (${counts['in-progress']})` },
              { value: 'resolved', label: `Risolti (${counts.resolved})` },
              { value: 'closed', label: `Chiusi (${counts.closed})` },
            ]}
          />
          <Select
            size="xs"
            placeholder="Priorità"
            w={140}
            value={priorityFilter}
            onChange={(v) => setPriorityFilter((v as PriorityFilter) ?? 'all')}
            data={[
              { value: 'all', label: 'Tutte priorità' },
              { value: '3', label: 'Urgente' },
              { value: '2', label: 'Alta' },
              { value: '1', label: 'Media' },
              { value: '0', label: 'Bassa' },
            ]}
          />
        </Group>
      </Card>

      <Card withBorder padding={0} radius="md">
        {ticketsQ.isError ? (
          <QueryErrorAlert error={ticketsQ.error} onRetry={() => void ticketsQ.refetch()} />
        ) : ticketsQ.isLoading ? (
          <Center py="xl"><Loader /></Center>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={IconHelp}
            title={tickets.length === 0 ? 'Nessun ticket aperto' : 'Nessun ticket corrisponde ai filtri'}
            description={tickets.length === 0
              ? 'Hai bisogno di assistenza? Apri un ticket: il team risponde entro 24h lavorative.'
              : 'Modifica i filtri o cerca con altri termini.'}
            actionLabel={tickets.length === 0 ? 'Nuovo ticket' : undefined}
            onAction={tickets.length === 0 ? open : undefined}
            color="teal"
          />
        ) : (
          <ScrollArea type="auto">
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={80}>#</Table.Th>
                  <Table.Th>Oggetto</Table.Th>
                  <Table.Th w={130}>Tag</Table.Th>
                  <Table.Th w={120}>Priorità</Table.Th>
                  <Table.Th w={150}>Stato</Table.Th>
                  <Table.Th w={150}>Aperto</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((t) => {
                  const s = statusInfo(t.status);
                  const p = priorityInfo(t.priority);
                  const StatusIcon = s.icon;
                  const subject = (t.message ?? '').split('\n')[0]?.slice(0, 80) || '(senza oggetto)';
                  return (
                    <Table.Tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setOpenTicketId(t.id)}>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <IconHash size={12} style={{ opacity: 0.5 }} />
                          <Text size="sm" fw={500}>{t.number ?? '—'}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td><Text size="sm" lineClamp={1}>{subject}</Text></Table.Td>
                      <Table.Td>
                        {t.ticketTag?.name
                          ? <Badge size="sm" variant="dot" color="gray">{t.ticketTag.name}</Badge>
                          : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={p.color} variant="light">{p.label}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={s.color} leftSection={<StatusIcon size={10} />}>
                          {s.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {t.creationDate ? dayjs(t.creationDate).format('DD/MM/YYYY HH:mm') : '—'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>

      <Modal opened={opened} onClose={close} title={<Group gap={6}><IconPlus size={18} /><Text fw={600}>Nuovo ticket</Text></Group>} centered size="lg" fullScreen={isMobile}>
        <Stack>
          <TextInput
            label="Oggetto"
            placeholder="Es. Problema con login app mobile"
            required
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            data-autofocus
          />
          <Textarea
            label="Descrizione"
            placeholder="Descrivi il problema con dettaglio (azioni, errore, schermate…)"
            minRows={4}
            autosize
            required
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
          />
          <Group grow>
            <Select
              label="Priorità"
              data={[
                { value: '3', label: '🔥 Urgente' },
                { value: '2', label: '⚠️ Alta' },
                { value: '1', label: '➡️ Media' },
                { value: '0', label: '↘️ Bassa' },
              ]}
              value={priority}
              onChange={(v) => setPriority(v ?? '1')}
              allowDeselect={false}
            />
            <Select
              label="Categoria"
              placeholder="Tag…"
              data={tags.map((t) => ({ value: t.id, label: t.name ?? t.tag ?? t.id }))}
              value={tagId}
              onChange={setTagId}
              searchable
              clearable
              leftSection={<IconTag size={14} />}
            />
          </Group>
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={14} />}>
            Riceverai una notifica appena un operatore prende in carico il ticket.
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={close}>Annulla</Button>
            <Button
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={!title.trim() || !body.trim()}
              leftSection={<IconSend size={14} />}
            >
              Invia ticket
            </Button>
          </Group>
        </Stack>
      </Modal>

      <TicketDetailDrawer
        ticketId={openTicketId}
        onClose={() => setOpenTicketId(null)}
      />
    </Stack>
  );
}

function TicketDetailDrawer({ ticketId, onClose }: { ticketId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [reply, setReply] = useState('');

  const ticketQ = useQuery({
    queryKey: ['ticket', ticketId],
    enabled: Boolean(ticketId),
    queryFn: async () => {
      const { data } = await fetchList<Ticket>('tickets', {
        filter: { id: { eq: ticketId! } },
        include: ['ticketTag'],
        page: { size: 1 },
      });
      return data[0] ?? null;
    },
  });

  const messagesQ = useQuery({
    queryKey: ['ticket-messages', ticketId],
    enabled: Boolean(ticketId),
    queryFn: async () => {
      try {
        const { data } = await fetchList<TicketMessage>('ticket-messages', {
          filter: { 'ticket.id': { eq: ticketId! } },
          include: ['user'],
          sort: ['creationDate'],
          page: { size: 100 },
        });
        return data;
      } catch {
        return [];
      }
    },
  });

  const reset = () => { setReply(''); };

  const sendReply = useMutation({
    mutationFn: () => createOne('ticket-messages', {
      attributes: { message: reply.trim() },
      relationships: { ticket: { data: { type: 'tickets', id: ticketId! } } },
    }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Risposta inviata' });
      reset();
      qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId] });
    },
    onError: (err) => notifications.show({ color: 'red', message: err instanceof Error ? err.message : 'Errore invio' }),
  });

  if (!ticketId) return null;
  const ticket = ticketQ.data;

  return (
    <Drawer
      opened
      onClose={() => { reset(); onClose(); }}
      position="right"
      size="lg"
      title={
        <Group gap={6}>
          <ActionIcon variant="subtle" aria-label="Indietro" onClick={onClose}>
            <IconArrowLeft size={16} />
          </ActionIcon>
          <IconHash size={14} style={{ opacity: 0.5 }} />
          <Text fw={600}>{ticket?.number ?? '…'}</Text>
        </Group>
      }
    >
      {ticketQ.isLoading || !ticket ? (
        <Center py="xl"><Loader /></Center>
      ) : (
        <Stack>
          {/* Header con metadati */}
          <Card withBorder padding="md" radius="md">
            <Stack gap="xs">
              <Group justify="space-between">
                <Group gap={6}>
                  {(() => {
                    const s = statusInfo(ticket.status);
                    const SIcon = s.icon;
                    return (
                      <Badge color={s.color} leftSection={<SIcon size={10} />}>
                        {s.label}
                      </Badge>
                    );
                  })()}
                  {(() => {
                    const p = priorityInfo(ticket.priority);
                    return <Badge color={p.color} variant="light">{p.label}</Badge>;
                  })()}
                  {ticket.ticketTag?.name && (
                    <Badge variant="dot" color="gray">{ticket.ticketTag.name}</Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  {ticket.creationDate ? dayjs(ticket.creationDate).format('DD/MM/YYYY HH:mm') : '—'}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                Aperto da: <strong>{[ticket.firstName, ticket.lastName].filter(Boolean).join(' ') || ticket.email || '—'}</strong>
              </Text>
              {ticket.closedDate && (
                <Text size="xs" c="dimmed">
                  Chiuso il: {dayjs(ticket.closedDate).format('DD/MM/YYYY HH:mm')}
                </Text>
              )}
            </Stack>
          </Card>

          {/* Messaggio iniziale */}
          <ConversationBubble
            author="Tu"
            initials={(user?.firstName?.[0] ?? user?.email?.[0] ?? 'T').toUpperCase()}
            color="blue"
            date={ticket.creationDate}
            body={ticket.message ?? ''}
          />

          {/* Conversazione */}
          {messagesQ.isLoading ? (
            <Loader size="sm" />
          ) : (
            (messagesQ.data ?? []).map((m) => {
              const author = m.user
                ? [m.user.firstName, m.user.lastName].filter(Boolean).join(' ') || m.user.email || 'Operatore'
                : 'Operatore';
              const initials = m.user?.firstName?.[0]?.toUpperCase() ?? 'O';
              return (
                <ConversationBubble
                  key={m.id}
                  author={author}
                  initials={initials}
                  color="teal"
                  date={m.creationDate}
                  body={m.message ?? ''}
                />
              );
            })
          )}

          {/* Risposta ufficiale (se presente) */}
          {ticket.answer && (
            <Card withBorder padding="md" radius="md" bg="var(--mantine-color-green-light)">
              <Text size="xs" fw={600} c="green" mb={4}>Risposta ufficiale</Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{ticket.answer}</Text>
            </Card>
          )}

          {/* Reply form */}
          {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
            <Card withBorder padding="md" radius="md">
              <Group gap={6} mb="xs">
                <IconMessageCircle size={14} />
                <Text size="sm" fw={500}>Aggiungi una risposta</Text>
              </Group>
              <Textarea
                placeholder="Scrivi un aggiornamento o una nuova informazione…"
                autosize
                minRows={3}
                value={reply}
                onChange={(e) => setReply(e.currentTarget.value)}
              />
              <Group justify="flex-end" mt="xs">
                <Button
                  size="sm"
                  leftSection={<IconSend size={14} />}
                  disabled={!reply.trim()}
                  loading={sendReply.isPending}
                  onClick={() => sendReply.mutate()}
                >
                  Invia
                </Button>
              </Group>
            </Card>
          )}
        </Stack>
      )}
    </Drawer>
  );
}

function ConversationBubble({
  author, initials, color, date, body,
}: {
  author: string;
  initials: string;
  color: string;
  date?: string;
  body: string;
}) {
  return (
    <Group align="flex-start" wrap="nowrap" gap="sm">
      <Avatar size="md" radius="xl" color={color}>{initials}</Avatar>
      <Card withBorder padding="sm" radius="md" style={{ flex: 1 }}>
        <Group justify="space-between" mb={4}>
          <Text size="sm" fw={600}>{author}</Text>
          {date && (
            <Text size="xs" c="dimmed">{dayjs(date).format('DD/MM HH:mm')}</Text>
          )}
        </Group>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{body || <Text component="span" c="dimmed">— vuoto —</Text>}</Text>
      </Card>
    </Group>
  );
}

